import { useState, useCallback } from "react";

import {
  DatabaseConnectionTest,
  type DatabaseConnectionValues,
} from "./DatabaseConnectionTest";
import { SchemaValidation } from "./SchemaValidation";

type SelfHostTargetConfigProps = {
  sourceType: string;
  orgName?: string;
  onConfigChange: (config: {
    connectionString: string | null;
    schema: string;
    connectionTested: boolean;
    schemaValidated: boolean;
    schemaAvailable: boolean;
  }) => void;
  disabled?: boolean;
};

const DEFAULT_CONNECTION_VALUES: DatabaseConnectionValues = {
  host: "",
  database: "",
  username: "",
  password: "",
};

export function SelfHostTargetConfig({
  sourceType,
  orgName,
  onConfigChange,
  disabled,
}: SelfHostTargetConfigProps) {
  const [connectionValues, setConnectionValues] =
    useState<DatabaseConnectionValues>(DEFAULT_CONNECTION_VALUES);
  const [connectionTested, setConnectionTested] = useState(false);
  const [connectionString, setConnectionString] = useState<string | null>(null);
  const [existingSchemas, setExistingSchemas] = useState<string[]>([]);
  const [schema, setSchema] = useState("");
  const [_schemaValidated, setSchemaValidated] = useState(false);
  const [_schemaAvailable, setSchemaAvailable] = useState(false);

  const handleConnectionFieldChange = (
    field: keyof DatabaseConnectionValues,
    value: string,
  ) => {
    setConnectionValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleConnectionTested = useCallback(
    (result: {
      success: boolean;
      connectionString: string | null;
      existingSchemas: string[];
    }) => {
      setConnectionTested(result.success);
      setConnectionString(result.connectionString);
      setExistingSchemas(result.existingSchemas);

      // Reset schema validation when connection changes
      if (!result.success) {
        setSchemaValidated(false);
        setSchemaAvailable(false);
      }

      onConfigChange({
        connectionString: result.connectionString,
        schema,
        connectionTested: result.success,
        schemaValidated: false,
        schemaAvailable: false,
      });
    },
    [schema, onConfigChange],
  );

  const handleSchemaChange = (value: string) => {
    setSchema(value);
    setSchemaValidated(false);
    setSchemaAvailable(false);

    onConfigChange({
      connectionString,
      schema: value,
      connectionTested,
      schemaValidated: false,
      schemaAvailable: false,
    });
  };

  const handleSchemaValidationChange = useCallback(
    (result: { validated: boolean; available: boolean }) => {
      setSchemaValidated(result.validated);
      setSchemaAvailable(result.available);

      onConfigChange({
        connectionString,
        schema,
        connectionTested,
        schemaValidated: result.validated,
        schemaAvailable: result.available,
      });
    },
    [connectionString, schema, connectionTested, onConfigChange],
  );

  return (
    <div className="space-y-6">
      {/* Step 1: Database Connection */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-4">
          Step 1: Database Connection
        </h3>
        <DatabaseConnectionTest
          values={connectionValues}
          onChange={handleConnectionFieldChange}
          onConnectionTested={handleConnectionTested}
          disabled={disabled}
          connectionTested={connectionTested}
          existingSchemas={existingSchemas}
        />
      </div>

      {/* Step 2: Schema Selection (only shown after connection test) */}
      {connectionTested && connectionString && (
        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">
            Step 2: Schema Selection
          </h3>
          <SchemaValidation
            value={schema}
            onChange={handleSchemaChange}
            connectionString={connectionString}
            sourceType={sourceType}
            orgName={orgName}
            existingSchemas={existingSchemas}
            onValidationChange={handleSchemaValidationChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
